'use client';

import React from 'react';
import queryString from 'query-string';
import defaultBouncerImg from '@/wag-react/components/floating-action-buttons/assets/pet-insurer-pug.png';
import dogFoodBowl from '@/wag-react/components/floating-action-buttons/assets/dog-bowl.png';
import { Container } from '../../components/Container';
import { WellnessArticleAuthorBlock } from './components/WellnessArticleAuthorBlock';
import { WellnessArticleBodyContainer } from './components/WellnessArticleBodyContainer';
import { WellnessArticleHeader } from './components/WellnessArticleHeader';
import { WellnessArticleRelatedArticles } from './components/WellnessArticleRelatedArticles';
import { WellnessArticleSocialShareIcons } from './components/WellnessArticleSocialShareIcons';
import { WellnessHeroHeaderImage } from './components/WellnessHeroHeaderImage';
import { WellnessPetInsurerWithFeaturedProviders } from './components/WellnessPetInsurerWithFeaturedProviders';
import { UTMContent, UTMMedium, UTMSource, UTMTerm } from '../../constants/query-string-values';
import { CamelizedWellnessArticleResponse } from '../../types/api';
import { Grid } from '../../wag-react/core-components/grid';
import { Box } from '../../wag-react/next-components/box';
import { TooltipBouncer } from '../../wag-react/components/floating-action-buttons';
import { Routes } from '../../constants';
import { theme } from '../../theme';
import { useIsDesktop } from '../../hooks';
export type WellnessArticlePageContainerProps = CamelizedWellnessArticleResponse & {
  slug: string;
  bgColor?: string;
};
export const WellnessArticlePageContainer = ({
  author,
  body,
  breadcrumbs,
  heroImageUrl,
  readTime,
  relatedArticles,
  shareUrls,
  slug,
  title,
  mobileBouncer: {
    ctaCopy = 'Need Pet Insurance?',
    ctaUrl = Routes.PetInsuranceQuoteLink,
    imageAltText = 'Pet Insurer Pug'
  },
  videoUrl
}: WellnessArticlePageContainerProps) => {
  const petInsuranceVariant = ctaCopy === 'Need Pet Insurance?';
  const isDesktop = useIsDesktop();
  const getImage = () => {
    if (petInsuranceVariant) {
      return defaultBouncerImg;
    }
    return dogFoodBowl;
  };
  const getBgColor = () => {
    if (petInsuranceVariant) {
      return theme.colors.mySin;
    }
    return theme.colors.catalinaBlue;
  };
  const getArrowPosition = () => {
    if (petInsuranceVariant) {
      return isDesktop ? '110px' : '102px';
    }
    return isDesktop ? '168px' : '80px';
  };
  const goToCta = () => {
    let destUrl = ctaUrl;
    if (destUrl === Routes.PetInsuranceQuoteLink) {
      destUrl = queryString.stringifyUrl({
        url: ctaUrl,
        query: {
          utm_campaign: slug,
          utm_content: UTMContent.Wellness,
          utm_medium: UTMMedium.Web,
          utm_source: UTMSource.Wag,
          utm_term: UTMTerm.MobileBouncer
        }
      });
    }
    window.open(destUrl, '_blank');
  };
  return <>
      <Container maxWidth={['100%', null, '1000px', '1600px']} px={['unset', '16px', '24px']} data-sentry-element="Container" data-sentry-source-file="index.tsx">
        <Grid gridTemplateColumns={['1fr', '100px 1fr 100px', 'repeat(3, 1fr)']} my={['0px', null, '24px']} data-sentry-element="Grid" data-sentry-source-file="index.tsx">
          <Box data-sentry-element="Box" data-sentry-source-file="index.tsx">
            <WellnessArticleSocialShareIcons display={['none', 'grid']} title={title} shareUrls={shareUrls} data-sentry-element="WellnessArticleSocialShareIcons" data-sentry-source-file="index.tsx" />
          </Box>
          <Box data-sentry-element="Box" data-sentry-source-file="index.tsx">
            <Box p={['16px', 'unset']} data-sentry-element="Box" data-sentry-source-file="index.tsx">
              <WellnessArticleHeader breadcrumbs={breadcrumbs} readTime={readTime} title={title} data-sentry-element="WellnessArticleHeader" data-sentry-source-file="index.tsx" />
              <WellnessArticleSocialShareIcons display={['grid', 'none']} position="unset" gridGap="0" title={title} shareUrls={shareUrls} data-sentry-element="WellnessArticleSocialShareIcons" data-sentry-source-file="index.tsx" />
            </Box>
            <Box data-sentry-element="Box" data-sentry-source-file="index.tsx">
              <WellnessHeroHeaderImage slug={slug} heroImageUrl={heroImageUrl} data-sentry-element="WellnessHeroHeaderImage" data-sentry-source-file="index.tsx" />
              {author && <WellnessArticleAuthorBlock value={author} />}

              <WellnessPetInsurerWithFeaturedProviders slug={slug} data-sentry-element="WellnessPetInsurerWithFeaturedProviders" data-sentry-source-file="index.tsx" />
            </Box>
            <Box p={['16px', 'unset']} mt="32px" data-sentry-element="Box" data-sentry-source-file="index.tsx">
              <WellnessArticleBodyContainer body={body} videoUrl={videoUrl} data-sentry-element="WellnessArticleBodyContainer" data-sentry-source-file="index.tsx" />
            </Box>
          </Box>
        </Grid>
      </Container>
      <Container maxWidth={['375px', '688px', '1024px', '1600px']} data-sentry-element="Container" data-sentry-source-file="index.tsx">
        <WellnessArticleRelatedArticles value={relatedArticles} data-sentry-element="WellnessArticleRelatedArticles" data-sentry-source-file="index.tsx" />
      </Container>

      <Box data-sentry-element="Box" data-sentry-source-file="index.tsx">
        <TooltipBouncer avatar={{
        src: getImage(),
        containerProps: {
          border: 'none',
          borderRadius: 0
        },
        imageProps: {
          alt: imageAltText,
          borderRadius: 0,
          style: {
            cursor: 'pointer'
          }
        }
      }} tooltip={{
        text: ctaCopy,
        bg: getBgColor(),
        px: '16px',
        tooltipColor: getBgColor(),
        py: '16px',
        marginLeft: getArrowPosition(),
        fontSize: '20px',
        // left: '90%',
        fontWeight: 'normal',
        maxWidth: '60vw',
        width: ['max-content']
      }} onClick={goToCta} bottom={['100px', '0px']} data-sentry-element="TooltipBouncer" data-sentry-source-file="index.tsx" />
      </Box>
    </>;
};